import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { isBubblesBogoTester } from '@/config/features';
import { BUBBLES_BOGO_CODE, BUBBLES_BOGO_LIMIT, isBubblesBogoEventLive } from '@/utils/bubblesBogo';

interface PromoStatus {
  promo_id: string;
  used_count: number;
  usage_limit: number | null;
  user_used: boolean;
}

export const useBubblesBogo = () => {
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const isTester = isBubblesBogoTester(user?.email, isAdmin);
  const visible = isTester || isBubblesBogoEventLive();

  const [status, setStatus] = useState<PromoStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!visible || !user) {
      setStatus(null);
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await (supabase as any).rpc('get_promo_status', {
        p_code: BUBBLES_BOGO_CODE,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      setStatus(row || null);
    } catch (e) {
      console.error('Error loading Bubbles BOGO status', e);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [visible, user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const limit = status?.usage_limit ?? BUBBLES_BOGO_LIMIT;
  const remaining = status ? Math.max(limit - status.used_count, 0) : null;
  const soldOut = remaining !== null && remaining <= 0;
  const alreadyUsed = !!status?.user_used && !isTester;
  const canRedeem = visible && !!status && !soldOut && !alreadyUsed;

  // Atomically reserve a slot on the server (limit + one-per-account enforced there)
  const claim = useCallback(async (confirm: boolean) => {
    const { data, error } = await (supabase as any).rpc('claim_promo', {
      p_code: BUBBLES_BOGO_CODE,
      p_confirm: confirm,
    });
    if (error) {
      console.error('Error claiming Bubbles BOGO', error);
      return { success: false as const, error: error.message as string };
    }
    return data as { success: boolean; error?: string; redemption_id?: string };
  }, []);

  const release = useCallback(async (redemptionId: string) => {
    const { error } = await (supabase as any).rpc('release_promo_redemption', {
      p_redemption_id: redemptionId,
    });
    if (error) console.error('Error releasing Bubbles BOGO', error);
  }, []);

  return { visible, canRedeem, soldOut, alreadyUsed, remaining, isTester, loading, refresh, claim, release };
};
